import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Button, Form, Modal, Select, Spin } from 'antd'
import { Controller, useForm } from 'react-hook-form'

import { errorMessage } from 'services/messager'
import { listResponsibles } from 'services/demands/demandsServices'
import useDebounce from 'hooks/useDebounce'

import styles from './index.module.scss'

const SetResponsibleModal = ({
	show,
	onCancel,
	onConfirm,
	loading = false,
	isLegalUser = false,
	teamId = '',
}) => {
	const user = useSelector(({ session }) => session)
	// TODO: debug search
	const [search, setSearch] = useState('')
	const [open, setOpen] = useState(false)
	const debouncedSearch = useDebounce(search, 400)

	const { control, handleSubmit, reset } = useForm({
		defaultValues: {
			responsible: undefined,
		},
	})
	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		// isError,
		// error,
		// refetch,
	} = useInfiniteQuery({
		queryKey: ['responsibles', debouncedSearch],
		queryFn: fetchResponsibles,
		getNextPageParam: (lastPage) => {
			const nextPage =
				lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined
			return nextPage
		},
		enabled: show && !isLegalUser,
		staleTime: 1000 * 30,
		refetchOnWindowFocus: false,
		retry: false,
	})

	const users = data ? data.pages.flatMap((page) => page.users) : []

	async function fetchResponsibles({ pageParam = 1 }) {
		try {
			const data = await listResponsibles(
				pageParam,
				20,
				debouncedSearch,
				teamId
			)

			return data
		} catch (error) {
			console.log(error)
			errorMessage({
				content: 'Erro ao listar usuários',
			})
			return { users: [], page: pageParam, total: 0, totalPages: 0 }
		}
	}

	async function onSubmit(data) {
		const responsibleUser =
			users.find((option) => option.id === data.responsible) ||
			(data.responsible === user.id
				? { id: user.id, name: user.name, email: user.email }
				: undefined)
		onConfirm({ ...data, responsibleUser })
		reset()
	}

	async function handleCancel() {
		onCancel()
		reset()
	}

	function handlePopupScroll(e) {
		const target = e.target
		const reachedBottom =
			target.scrollTop + target.offsetHeight >= target.scrollHeight - 10

		if (reachedBottom && hasNextPage && !isFetchingNextPage) {
			fetchNextPage()
		}
	}

	const footerButtons = () => {
		return [
			<Button key="cancel-responsible" onClick={handleCancel}>
				Cancelar
			</Button>,
			<Button
				key="confirm-responsible"
				onClick={
					isLegalUser
						? () => onSubmit({ responsible: user.id })
						: handleSubmit(onSubmit)
				}
				loading={loading}
				disabled={loading}>
				Confirmar
			</Button>,
		]
	}

	const getSelectOptions = useCallback(() => {
		return (
			<>
				{users.map((user) => (
					<Select.Option key={user.id} value={user.id}>
						{user.name}
					</Select.Option>
				))}
				{isFetchingNextPage && (
					<Select.Option disabled>Carregando mais...</Select.Option>
				)}
			</>
		)
	}, [users, isFetchingNextPage])

	if (isLegalUser) {
		return (
			<Modal
				open={show}
				onCancel={handleCancel}
				destroyOnClose={true}
				title="Atribuir responsável"
				footer={footerButtons()}>
				<p>Atribuir a mim responsabilidade sobre a demanda</p>
			</Modal>
		)
	}

	return (
		<Modal
			open={show}
			onCancel={handleCancel}
			destroyOnClose={true}
			title="Atribuir demanda para um responsável"
			footer={footerButtons()}>
			<p className={styles.label}>
				Selecione a pessoa responsável pela demanda
			</p>
			<Form layout="vertical" requiredMark={false}>
				<Form.Item required>
					<Controller
						name="responsible"
						control={control}
						rules={{ required: 'Selecione um responsável' }}
						render={({ field, fieldState }) => (
							<>
								<Select
									{...field}
									placeholder="Selecione um responsável"
									open={open}
									onDropdownVisibleChange={setOpen}
									onChange={(value) => field.onChange(value)}
									disabled={isLoading || loading}
									showSearch={false}
									// onSearch={(value) => {
									// 	TODO: investigate why is calling setSearch with empty string after search
									// 	console.log('onSearch setSearch', { value })
									// 	setSearch(value)
									// 	setOpen(true)
									// }}
									filterOption={false}
									notFoundContent={isLoading ? <Spin size="small" /> : null}
									onPopupScroll={handlePopupScroll}>
									{isLoading && (
										<Select.Option disabled>Carregando...</Select.Option>
									)}
									{getSelectOptions()}
								</Select>

								{fieldState.error && (
									<span className={styles.errorMessage}>
										{fieldState.error.message}
									</span>
								)}
							</>
						)}
					/>
				</Form.Item>
			</Form>
		</Modal>
	)
}

SetResponsibleModal.propTypes = {
	show: PropTypes.bool,
	onCancel: PropTypes.func,
	onConfirm: PropTypes.func,
	loading: PropTypes.bool,
	isLegalUser: PropTypes.bool,
	teamId: PropTypes.string,
}

export default SetResponsibleModal
